/* IntegrationInfrastructurePage.jsx — Full Integration Infrastructure surface.
 *
 * The dedicated /integrations route. Houses the IntegrationHealthPanel
 * + dedicated cards for Email Intelligence, Calendar Coordination,
 * Signing Status, and Compliance Readiness — each surfaced cleanly with
 * the existing glassmorphism + cinematic hero language.
 */
import React, { useEffect, useState } from "react";
import {
  Plug, Mail, Calendar, PenLine, BadgeCheck, Network, AlertOctagon,
} from "lucide-react";
import { api } from "../lib/api";
import { IntegrationHealthPanel } from "../components/mission-control/IntegrationHealthPanel";

const STATUS_TONE = {
  passed:    { fg: "#047857", bg: "rgba(16,185,129,0.10)", dot: "#10B981" },
  pending:   { fg: "#92400E", bg: "rgba(245,158,11,0.10)", dot: "#F59E0B" },
  failed:    { fg: "#9F1239", bg: "rgba(244,63,94,0.08)",  dot: "#F43F5E" },
  sent:      { fg: "#475569", bg: "rgba(148,163,184,0.10)", dot: "#94A3B8" },
  viewed:    { fg: "#4338CA", bg: "rgba(99,102,241,0.10)",  dot: "#818CF8" },
  signed:    { fg: "#047857", bg: "rgba(16,185,129,0.10)", dot: "#10B981" },
  completed: { fg: "#047857", bg: "rgba(16,185,129,0.14)", dot: "#10B981" },
  stalled:   { fg: "#9F1239", bg: "rgba(244,63,94,0.08)",  dot: "#F43F5E" },
};

/* ─── Hero ─────────────────────────────────────────────────────── */
const Hero = ({ summary, narrative }) => (
  <header
    className="relative overflow-hidden rounded-3xl mb-7 px-6 sm:px-9 lg:px-10 py-7 sm:py-9"
    data-testid="integrations-page-hero"
    style={{
      background: "linear-gradient(135deg, #04070F 0%, #131A3A 45%, #1E2C70 100%)",
      boxShadow: "0 30px 80px -36px rgba(15,23,42,0.55), inset 0 0 0 1px rgba(255,255,255,0.05)",
    }}
  >
    <span aria-hidden className="absolute -top-16 -left-12 w-[360px] h-[360px] rounded-full blur-3xl pointer-events-none"
      style={{ background: "radial-gradient(circle, rgba(56,189,248,0.32), transparent 65%)" }} />
    <span aria-hidden className="absolute -bottom-20 right-12 w-[420px] h-[420px] rounded-full blur-3xl pointer-events-none"
      style={{ background: "radial-gradient(circle, rgba(167,139,250,0.18), transparent 70%)" }} />
    <div className="relative">
      <div className="inline-flex items-center gap-2 mb-3">
        <Network size={13} className="text-sky-300" strokeWidth={2.2} />
        <p className="text-[10.5px] uppercase tracking-[0.22em] font-bold text-sky-200/90">
          Integration Infrastructure
        </p>
      </div>
      <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-[1.05] mb-3"
          data-testid="integrations-page-title">
        Operational Connectivity
      </h1>
      <p className="text-[14px] sm:text-[15px] text-white/65 leading-relaxed max-w-2xl">
        External infrastructure layer connecting ChainFlow OS to the wider property ecosystem.
      </p>
      {narrative ? (
        <p className="mt-4 text-[12.5px] text-white/55 leading-relaxed max-w-2xl">
          {narrative}
        </p>
      ) : null}
    </div>
  </header>
);

const SectionHead = ({ icon: Icon, eyebrow, title, subtitle }) => (
  <div className="flex items-center gap-2.5 mb-3.5">
    <div className="w-7 h-7 rounded-md inline-flex items-center justify-center"
      style={{ background: "rgba(99,102,241,0.10)", color: "#4338CA" }}>
      <Icon size={13} strokeWidth={2.3} />
    </div>
    <div className="min-w-0">
      <p className="text-[10px] uppercase tracking-[0.16em] font-bold text-violet-700">{eyebrow}</p>
      <h2 className="text-[13.5px] font-semibold text-slate-900 truncate">{title}</h2>
      {subtitle ? (
        <p className="text-[11px] text-slate-500 truncate">{subtitle}</p>
      ) : null}
    </div>
  </div>
);

/* ─── Email Intelligence Card ──────────────────────────────────── */
const EmailIntelCard = ({ data }) => (
  <div className="gp-card p-4" data-testid="integrations-email-intel">
    <SectionHead icon={Mail} eyebrow="Email Intelligence" title="Inbound → operational events" />
    <p className="text-[12px] text-slate-600 leading-snug mb-3">{data?.narrative}</p>
    {(data?.items || []).length === 0 ? (
      <p className="text-[11.5px] text-slate-500 italic">No classified emails yet.</p>
    ) : (
      <div className="space-y-1.5">
        {(data.items || []).map((it, i) => (
          <div key={i} className="flex items-center gap-3"
               data-testid={`integrations-email-item-${i}`}>
            <span aria-hidden className="w-1.5 h-1.5 rounded-full bg-sky-500 shrink-0" />
            <p className="text-[12px] font-medium text-slate-800 flex-1 truncate">
              {it.subject}
            </p>
            <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-sky-700 shrink-0">
              {it.classified_event}
            </span>
          </div>
        ))}
      </div>
    )}
  </div>
);

/* ─── Calendar Coordination Card ───────────────────────────────── */
const CalendarCard = ({ data }) => (
  <div className="gp-card p-4" data-testid="integrations-calendar">
    <SectionHead icon={Calendar} eyebrow="Calendar Coordination"
      title="Upcoming events · conflicts" />
    <p className="text-[12px] text-slate-600 leading-snug mb-3">{data?.narrative}</p>
    {(data?.conflicts || []).length ? (
      <div className="mb-3 rounded-md p-2"
        style={{ background: "rgba(244,63,94,0.08)" }}>
        <p className="text-[10px] uppercase tracking-[0.14em] font-bold text-rose-700 mb-1">
          Conflicts detected
        </p>
        {data.conflicts.slice(0, 3).map((c, i) => (
          <p key={i} className="text-[11px] text-slate-700"
             data-testid={`integrations-cal-conflict-${i}`}>
            {c.a_title} ↔ {c.b_title}
          </p>
        ))}
      </div>
    ) : null}
    {(data?.upcoming || []).map((e, i) => (
      <div key={i} className="flex items-center gap-3 mb-1.5"
           data-testid={`integrations-cal-event-${i}`}>
        <span aria-hidden className="w-1.5 h-1.5 rounded-full bg-violet-500 shrink-0" />
        <p className="text-[12px] font-medium text-slate-800 flex-1 truncate">{e.title}</p>
        <p className="text-[10.5px] tabular-nums text-slate-500">
          {new Date(e.starts_at).toLocaleString()}
        </p>
      </div>
    ))}
  </div>
);

/* ─── Signing Status Card ──────────────────────────────────────── */
const SigningCard = ({ data }) => (
  <div className="gp-card p-4" data-testid="integrations-signing">
    <SectionHead icon={PenLine} eyebrow="Document Signing"
      title={`${data?.counts?.completed || 0} completed · ${data?.open_items?.length || 0} open`} />
    <p className="text-[12px] text-slate-600 leading-snug mb-3">{data?.narrative}</p>
    {(data?.open_items || []).map((s, i) => {
      const t = STATUS_TONE[s.status] || STATUS_TONE.sent;
      return (
        <div key={i} className="flex items-center gap-3 mb-1.5"
             data-testid={`integrations-sign-${i}`}>
          <span aria-hidden className="w-1.5 h-1.5 rounded-full shrink-0"
            style={{ background: t.dot }} />
          <p className="text-[12px] font-medium text-slate-800 flex-1 truncate">
            {s.envelope}
            {s.deal_name ? (
              <span className="text-slate-400"> · {s.deal_name}</span>
            ) : null}
          </p>
          <span className="text-[10px] font-bold uppercase tracking-[0.12em] shrink-0"
            style={{ color: t.fg }}>
            {s.status}
          </span>
        </div>
      );
    })}
  </div>
);

/* ─── Compliance Readiness Card ────────────────────────────────── */
const ComplianceCard = ({ data }) => (
  <div className="gp-card p-4" data-testid="integrations-compliance">
    <SectionHead icon={BadgeCheck} eyebrow="Compliance Readiness"
      title="AML, ID, POF, sanctions screening" />
    <p className="text-[12px] text-slate-600 leading-snug mb-3">{data?.narrative}</p>
    {(data?.blockers || []).length ? (
      <div className="space-y-1.5">
        {data.blockers.map((b, i) => {
          const t = STATUS_TONE[b.status] || STATUS_TONE.pending;
          return (
            <div key={i} className="flex items-center gap-3 rounded-md p-2"
                 style={{ background: t.bg }}
                 data-testid={`integrations-compliance-blocker-${i}`}>
              <span aria-hidden className="w-1.5 h-1.5 rounded-full shrink-0"
                style={{ background: t.dot }} />
              <p className="text-[12px] font-semibold text-slate-900 flex-1 truncate">
                {b.check_type}
                {b.deal_name ? (
                  <span className="text-slate-500"> · {b.deal_name}</span>
                ) : null}
              </p>
              <span className="text-[10px] font-bold uppercase tracking-[0.12em] shrink-0"
                style={{ color: t.fg }}>
                {b.status}
              </span>
            </div>
          );
        })}
      </div>
    ) : (
      <p className="text-[11.5px] text-slate-500 italic">No outstanding compliance blockers.</p>
    )}
  </div>
);

/* ═══════════════════════════════════════════════════════════════════
   Page
   ─────────────────────────────────────────────────────────────────── */
export default function IntegrationInfrastructurePage({ embedded = false }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    (async () => {
      try {
        const { data: resp } = await api.get("/api/integrations/overview");
        if (alive) setData(resp?.integration || null);
      } catch (err) {
        if (alive) setError(err?.response?.status === 403
          ? "Integration Infrastructure is available to admin / director / manager roles only."
          : "Could not load Integration Infrastructure.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const wrapperClass = embedded
    ? "min-w-0"
    : "md:ml-[var(--cf-sidebar-w,300px)] transition-[margin-left] duration-300 ease-out min-h-screen px-5 sm:px-7 lg:px-9 pt-6 pb-12 overflow-x-hidden";

  return (
    <div className={wrapperClass}
         data-testid="integrations-page">
      {error ? (
        <div className="gp-card p-6 text-center max-w-xl mx-auto"
             data-testid="integrations-error">
          <Plug size={20} className="text-violet-500 mx-auto mb-3" />
          <p className="text-[13.5px] font-semibold text-slate-900 mb-1">{error}</p>
        </div>
      ) : loading ? (
        <div className="gp-card p-6 cf-pulse">
          <div className="h-3 w-40 bg-slate-100 rounded mb-3" />
          <div className="h-4 w-72 bg-slate-100 rounded mb-2" />
        </div>
      ) : data ? (
        <>
          <Hero summary={data.summary} narrative={data.narrative} />
          <IntegrationHealthPanel />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-7">
            <EmailIntelCard data={data.email_intelligence} />
            <CalendarCard   data={data.calendar_coordination} />
            <SigningCard    data={data.signing_status} />
            <ComplianceCard data={data.compliance_readiness} />
          </div>
        </>
      ) : null}
    </div>
  );
}
